import { useState, useRef, useEffect, useCallback } from 'react';
import { animate, stagger, spring, createTimeline } from 'animejs';
import { PortableText } from '@portabletext/react';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { urlFor } from './sanity/client';
import SettingsPanel from './components/SettingsPanel';
import EventModal from './components/EventModal';
import ControlDock from './components/ControlDock';

const CareerDial = ({ settings, events = {} }) => {
  const { flags, toggleFlag, resetToDefault } = useFeatureFlags();
  const startYear = 2018;

  const endYear = 2025;
  const totalMonths = (endYear - startYear + 1) * 12;

  // Use events from Sanity (events prop is already an object keyed by monthIndex)
  const timelineEvents = events;

  const [currentMonth, setCurrentMonth] = useState(0);
  const [prevYear, setPrevYear] = useState(startYear);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, month: 0, time: 0 });
  const [dragOffset, setDragOffset] = useState(0); // Pixel offset during drag
  const [momentumOffset, setMomentumOffset] = useState(0);
  const [barSpacing, setBarSpacing] = useState(18);
  const [currentSection, setCurrentSection] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMomentumScrolling, setIsMomentumScrolling] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const targetMonthRef = useRef(0);
  const animationIntervalRef = useRef(null);
  const momentumAnimationRef = useRef(null);
  const autoPlayRef = useRef(null);
  const lastDragPositionRef = useRef({ x: 0, time: 0 });

  const containerRef = useRef(null);
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const monthDisplayRef = useRef(null);
  const yearDisplayRef = useRef(null);
  const eventCardRef = useRef(null);

  // Set initial month to most recent event once events load
  useEffect(() => {
    if (!hasInitialized && Object.keys(timelineEvents).length > 0) {
      const monthsWithEvents = Object.keys(timelineEvents).map(Number);
      const maxMonth = Math.max(...monthsWithEvents);
      setCurrentMonth(maxMonth);
      targetMonthRef.current = maxMonth;
      setHasInitialized(true);
    }
  }, [timelineEvents, hasInitialized]);

  // Scroll to hash anchor on page load (for #dial)
  useEffect(() => {
    if (hasInitialized && window.location.hash) {
      const timer = setTimeout(() => {
        const element = document.querySelector(window.location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasInitialized]);

  // Staggered entrance animation for timeline bars - runs after DOM is ready
  useEffect(() => {
    if (hasInitialized) {
      // Small delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        const bars = document.querySelectorAll('.timeline-bar');
        bars.forEach(bar => {
          bar.style.transformOrigin = 'bottom';
        });

        animate('.timeline-bar', {
          scaleY: [0, 1],
          opacity: [0, 1],
          delay: stagger(5, { from: 'center' }),
          ease: spring(1, 80, 10, 0),
          duration: 800
        });
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [hasInitialized]);

  // Stepped animation when clicking on a bar
  const animateToMonth = useCallback((targetMonth) => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }

    targetMonthRef.current = targetMonth;
    setIsAnimating(true);

    const distance = Math.abs(targetMonth - currentMonth);
    const step = currentMonth < targetMonth ? 1 : -1;

    // Scale duration based on distance: max 3s for full timeline
    const maxDuration = 3000; // 3 seconds for full timeline
    const totalDuration = (distance / totalMonths) * maxDuration;
    const stepDuration = Math.max(10, totalDuration / distance); // Min 10ms per step

    animationIntervalRef.current = setInterval(() => {
      setCurrentMonth((prev) => {
        const next = prev + step;

        // Check if we've reached the target
        if ((step > 0 && next >= targetMonth) || (step < 0 && next <= targetMonth)) {
          clearInterval(animationIntervalRef.current);
          setIsAnimating(false);
          return targetMonth;
        }

        return next;
      });
    }, stepDuration);
  }, [currentMonth, totalMonths]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, []);

  // Auto Play Logic
  useEffect(() => {
    if (isPlaying) {
      const play = () => {
        setCurrentMonth((prev) => {
          // Loop back to start if at the end
          if (prev >= totalMonths - 1) {
            return 0;
          }
          // Advance by small increment for smooth scroll (simulated by frequent updates)
          // Since we use integer months, we control speed by interval duration
          return prev + 1;
        });
      };

      // Speed: 1 month every 800ms
      autoPlayRef.current = setInterval(play, 800);
    } else {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPlaying, totalMonths]);

  const toggleAutoPlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const scrollToSection = (sectionIndex) => {
    const refs = [section1Ref, section2Ref, section3Ref];
    refs[sectionIndex].current?.scrollIntoView({ behavior: 'smooth' });
    setCurrentSection(sectionIndex);
  };

  const getMonthYear = (monthIndex) => {
    const year = startYear + Math.floor(monthIndex / 12);
    const month = monthIndex % 12;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return { year, month: monthNames[month] };
  };

  const jumpToYear = (targetYear) => {
    const monthIndex = (targetYear - startYear) * 12;
    animateToMonth(monthIndex);
  };



  // Timeline scroll management - horizontal scroll only on timeline area
  useEffect(() => {
    const timelineElement = containerRef.current;

    if (!timelineElement) return;

    const handleTimelineWheel = (e) => {
      // Only handle horizontal scroll (trackpad swipe or shift+wheel)
      const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);

      if (!isHorizontalScroll) {
        // Let vertical scroll pass through for section navigation
        return;
      }

      // Prevent horizontal scroll from affecting page
      e.preventDefault();
      e.stopPropagation();

      let monthDelta = Math.round(e.deltaX / 20);
      monthDelta = Math.max(-2, Math.min(2, monthDelta)); // Cap at ±2 months per scroll

      setCurrentMonth((prevMonth) => {
        return Math.max(0, Math.min(totalMonths - 1, prevMonth + monthDelta));
      });
    };

    timelineElement.addEventListener('wheel', handleTimelineWheel, { passive: false });
    return () => {
      timelineElement.removeEventListener('wheel', handleTimelineWheel);
    };
  }, [totalMonths]);

  const { year, month } = getMonthYear(currentMonth);
  const currentEvent = timelineEvents[currentMonth];

  // Portable Text components for styling
  const portableTextComponents = {
    block: {
      normal: ({ children }) => <span>{children}</span>,
    },
    marks: {
      strong: ({ children }) => <strong className="font-bold" style={{ color: 'oklch(1 0 0)' }}>{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      white: ({ children }) => <span style={{ color: 'oklch(1 0 0)' }}>{children}</span>,
      yellow: ({ children }) => <span style={{ color: 'oklch(0.85 0.15 85)' }}>{children}</span>,
      link: ({ value, children }) => {
        const target = (value?.href || '').startsWith('http') ? '_blank' : undefined;
        return (
          <a
            href={value?.href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            className="hover:opacity-80 transition-opacity underline"
            style={{ color: 'oklch(1 0 0)' }}
          >
            {children}
          </a>
        );
      },
    },
  };

  // Track year changes and animate transitions
  useEffect(() => {
    if (year !== prevYear && prevYear !== startYear) {
      // Timeline sequencing for year changes
      const timeline = createTimeline({
        easing: 'easeOutExpo'
      });

      timeline
        .add({
          targets: monthDisplayRef.current,
          opacity: [1, 0],
          translateY: [0, -10],
          duration: 150,
          easing: 'easeInQuad'
        })
        .add({
          targets: monthDisplayRef.current,
          opacity: [0, 1],
          translateY: [10, 0],
          duration: 200,
          easing: 'easeOutCubic'
        }, '-=0')
        .add({
          targets: '.year-display span',
          scale: [1.1, 1],
          duration: 250,
          easing: 'easeOutElastic(1, .8)'
        }, '-=100');

      setPrevYear(year);
    }
  }, [year, prevYear, startYear]);

  // Animate event card with keyframes when currentEvent changes
  useEffect(() => {
    if (currentEvent && eventCardRef.current) {
      // Keyframe drop-and-settle animation
      animate('.event-title, .event-image', {
        keyframes: [
          { opacity: 0, translateY: -30 },  // Start above
          { opacity: 0.6, translateY: 8 },   // Drop below (overshoot)
          { opacity: 1, translateY: 0 }      // Settle into place
        ],
        duration: 500,
        ease: 'out(3)'
      });
    }
  }, [currentEvent]);

  // Split year into digits for display
  const yearStr = year.toString();

  // Update bar spacing on window resize - ONLY if zoom controls are disabled
  useEffect(() => {
    if (flags.zoomControls) return;

    const updateBarSpacing = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setBarSpacing(12);
      } else if (width < 768) {
        setBarSpacing(15);
      } else {
        setBarSpacing(18);
      }
    };

    updateBarSpacing();
    window.addEventListener('resize', updateBarSpacing);
    return () => window.removeEventListener('resize', updateBarSpacing);
  }, [flags.zoomControls]);

  // Track which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = parseInt(entry.target.getAttribute('data-section'));
            setCurrentSection(section);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (section1Ref.current) observer.observe(section1Ref.current);
    if (section2Ref.current) observer.observe(section2Ref.current);
    if (section3Ref.current) observer.observe(section3Ref.current);

    return () => observer.disconnect();
  }, []);

  // Mouse/touch drag handlers
  const handleMouseDown = (e) => {
    // Cancel any ongoing momentum
    if (momentumAnimationRef.current) {
      cancelAnimationFrame(momentumAnimationRef.current);
      setIsMomentumScrolling(false);
    }

    const now = Date.now();
    setIsDragging(true);
    setDragStart({ x: e.clientX, month: currentMonth, time: now });
    lastDragPositionRef.current = { x: e.clientX, time: now };
    setDragOffset(0);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const now = Date.now();
    const deltaX = e.clientX - dragStart.x;

    setDragOffset(deltaX);

    // Update currentMonth in real-time as we cross bars for tactile feedback
    const monthDelta = -Math.round(deltaX / barSpacing);
    const newMonth = Math.max(0, Math.min(totalMonths - 1, dragStart.month + monthDelta));
    if (newMonth !== currentMonth) {
      setCurrentMonth(newMonth);
    }

    lastDragPositionRef.current = { x: e.clientX, time: now };
  }, [isDragging, dragStart, barSpacing, totalMonths, currentMonth]);

  const handleMouseUp = useCallback(() => {
    const now = Date.now();
    const timeDelta = now - lastDragPositionRef.current.time;
    const distanceDelta = lastDragPositionRef.current.x - dragStart.x;

    // Calculate velocity (pixels per ms) - positive = dragging right
    const velocity = timeDelta > 0 ? distanceDelta / timeDelta : 0;

    setIsDragging(false);
    // Only apply momentum if there was actual movement (not just a click)
    if (Math.abs(distanceDelta) > 5 && Math.abs(velocity) > 0.5 && timeDelta < 100) {
      setIsMomentumScrolling(true);

      let currentVelocity = velocity;
      let currentOffset = dragOffset;

      const animate = () => {
        // Apply deceleration with progressive resistance based on bars crossed
        const barsCrossed = Math.abs(currentOffset / barSpacing);
        const dynamicDeceleration = 0.90 - (barsCrossed * 0.002); // More friction, faster slowdown

        currentVelocity *= Math.max(0.80, dynamicDeceleration);
        currentOffset += currentVelocity * 12;

        setMomentumOffset(currentOffset);

        // Continue if velocity is significant
        if (Math.abs(currentVelocity) > 0.02) {
          momentumAnimationRef.current = requestAnimationFrame(animate);
        } else {
          // Snap to nearest month when momentum stops
          const monthDelta = -Math.round(currentOffset / barSpacing);
          const newMonth = Math.max(0, Math.min(totalMonths - 1, dragStart.month + monthDelta));
          setCurrentMonth(newMonth);
          setIsMomentumScrolling(false);
          setMomentumOffset(0);
          setDragOffset(0);
        }
      };

      momentumAnimationRef.current = requestAnimationFrame(animate);
    } else {
      // No momentum - check if it was a click (minimal movement and time)
      if (Math.abs(distanceDelta) < 5 && timeDelta < 200) {
        // This was a click, not a drag - let the onClick handler work
        return;
      }

      // Small drag, just snap to nearest month
      const monthDelta = -Math.round(dragOffset / barSpacing);
      const newMonth = Math.max(0, Math.min(totalMonths - 1, dragStart.month + monthDelta));
      setCurrentMonth(newMonth);
      setDragOffset(0);
    }
  }, [dragOffset, barSpacing, dragStart, totalMonths]);

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    // Cancel any ongoing momentum
    if (momentumAnimationRef.current) {
      cancelAnimationFrame(momentumAnimationRef.current);
      setIsMomentumScrolling(false);
    }

    const now = Date.now();
    setIsDragging(true);
    setDragStart({ x: e.touches[0].clientX, month: currentMonth, time: now });
    lastDragPositionRef.current = { x: e.touches[0].clientX, time: now };
    setDragOffset(0);
    // Don't prevent default - let vertical scroll work for sections
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;

    const now = Date.now();
    const deltaX = e.touches[0].clientX - dragStart.x;

    setDragOffset(deltaX);

    // Update currentMonth in real-time as we cross bars for tactile feedback
    const monthDelta = -Math.round(deltaX / barSpacing);
    const newMonth = Math.max(0, Math.min(totalMonths - 1, dragStart.month + monthDelta));
    if (newMonth !== currentMonth) {
      setCurrentMonth(newMonth);
    }

    lastDragPositionRef.current = { x: e.touches[0].clientX, time: now };

    // Only prevent default if we're actually scrubbing (significant horizontal movement)
    if (Math.abs(deltaX) > 10) {
      e.preventDefault();
    }
  }, [isDragging, dragStart, barSpacing, totalMonths, currentMonth]);

  const handleTouchEnd = useCallback((e) => {
    const now = Date.now();
    const timeDelta = now - lastDragPositionRef.current.time;
    const distanceDelta = lastDragPositionRef.current.x - dragStart.x;

    setIsDragging(false);

    // Check if it was a tap (minimal movement and time)
    if (Math.abs(distanceDelta) < 5 && timeDelta < 200) {
      // This was a tap - find which bar was tapped and animate to it
      const target = e.target.closest('[data-month]');
      if (target) {
        const monthIndex = parseInt(target.getAttribute('data-month'));
        animateToMonth(monthIndex);
      }
      setDragOffset(0);
      return;
    }

    // No momentum on mobile - just snap to nearest month
    const monthDelta = -Math.round(dragOffset / barSpacing);
    const newMonth = Math.max(0, Math.min(totalMonths - 1, dragStart.month + monthDelta));
    setCurrentMonth(newMonth);
    setDragOffset(0);
  }, [dragOffset, barSpacing, dragStart, totalMonths, animateToMonth]);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setCurrentMonth(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setCurrentMonth(prev => Math.min(totalMonths - 1, prev + 1));
          break;
        case ' ': // Spacebar
          e.preventDefault();
          if (flags.autoPlay) {
            toggleAutoPlay();
          }
          break;
        case 'Escape':
          if (selectedEvent) {
            setSelectedEvent(null);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [totalMonths, flags.autoPlay, selectedEvent, toggleAutoPlay]);

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <div
      className="bg-black select-none h-screen overflow-y-scroll scrollbar-hide"
      style={{
        scrollSnapType: 'y mandatory',
        overscrollBehavior: 'none'
      }}
    >
      {/* Section 1: Introduction */}
      <section
        ref={section1Ref}
        data-section="0"
        className="min-h-screen flex flex-col items-center justify-center p-10 relative"
        style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
      >
        <div className="text-center max-w-[295px] min-[400px]:max-w-[360px] sm:max-w-[523px]">
          <div className="text-[#959595] text-base min-[400px]:text-[18px] min-[440px]:text-[20px] sm:text-2xl font-medium leading-[1.2] tracking-[-0.02em]">
            {settings?.introText ? (
              Array.isArray(settings.introText) ? (
                <PortableText value={settings.introText} components={portableTextComponents} />
              ) : (
                settings.introText
              )
            ) : (
              "Hi, I'm Peter. A product designer with 6+ years of experience. This site presents a timeline of my professional journey, with notable roles, activities and projects highlighted."
            )}
          </div>
        </div>

      </section>

      {/* Section 2: Context */}
      <section
        ref={section2Ref}
        data-section="1"
        className="min-h-screen flex flex-col items-center justify-center p-10 relative"
        style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
      >
        <div className="text-center max-w-[295px] min-[400px]:max-w-[360px] sm:max-w-[542px]">
          <div className="text-[#959595] text-base min-[400px]:text-[18px] min-[440px]:text-[20px] sm:text-2xl font-medium leading-[1.2] tracking-[-0.02em]">
            {settings?.contextText ? (
              Array.isArray(settings.contextText) ? (
                <PortableText value={settings.contextText} components={portableTextComponents} />
              ) : (
                settings.contextText
              )
            ) : (
              "The roles and projects highlighted here are the ones I can remember. To get an overall sense of my work, visit layers.to/abu. Case studies are available on request."
            )}
          </div>
        </div>
      </section>

      {/* Section 3: Timeline */}
      <section
        id="dial"
        ref={section3Ref}
        data-section="2"
        className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-8 pb-20 sm:pb-32"
        style={{
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          paddingTop: 'clamp(32px, 8vh, 80px)'
        }}
      >
        {/* Top Description */}
        {/* Top Description */}
        <div className="text-center text-[#959595] text-sm min-[400px]:text-base sm:text-base font-normal leading-[1.2] tracking-[-0.02em] px-4 max-w-[295px] min-[400px]:max-w-[360px] sm:max-w-[373px] mb-12">
          {settings?.timelineDescription ? (
            Array.isArray(settings.timelineDescription) ? (
              <PortableText value={settings.timelineDescription} components={portableTextComponents} />
            ) : (
              settings.timelineDescription
            )
          ) : (
            "Scroll or drag to navigate through time to explore key moments, projects, and career milestones"
          )}
        </div>

        {/* Date/Event/Project Container */}
        <div
          className="flex flex-col items-center gap-5"
        >
          {/* Year Display */}
          <div className="text-center overflow-hidden">
            <div
              ref={monthDisplayRef}
              key={month}
              className="text-[#959595] text-base font-medium leading-[1.2] tracking-[-0.02em] mb-2"
            >
              {month}
            </div>
            <div ref={yearDisplayRef} className="text-white font-bold tracking-tight flex justify-center year-display" style={{ fontSize: '48px', lineHeight: '1.2' }}>
              {yearStr.split('').map((digit, i) => (
                <span
                  key={`${i}-${digit}`}
                  className="inline-block"
                  style={{
                    transformOrigin: 'center bottom'
                  }}
                >
                  {digit}
                </span>
              ))}
            </div>
          </div>

          {/* Event Info & Project Image Container - fixed height to prevent jumping */}
          <div
            ref={eventCardRef}
            className={`flex flex-col items-center gap-5 sm:w-[320px] ${flags.eventModals && currentEvent && (
              currentEvent.detailedDescription ||
              (currentEvent.outcomes && currentEvent.outcomes.length > 0) ||
              (currentEvent.techStack && currentEvent.techStack.length > 0) ||
              (currentEvent.links && currentEvent.links.length > 0) ||
              (currentEvent.imageGallery && currentEvent.imageGallery.length > 0)
            ) ? 'cursor-pointer hover:opacity-80' : ''
              }`}
            style={{ width: 'clamp(295px, calc(295px + (360 - 295) * ((100vw - 400px) / 40)), 360px)' }}
            onClick={() => {
              if (
                flags.eventModals &&
                currentEvent &&
                (
                  currentEvent.detailedDescription ||
                  (currentEvent.outcomes && currentEvent.outcomes.length > 0) ||
                  (currentEvent.techStack && currentEvent.techStack.length > 0) ||
                  (currentEvent.links && currentEvent.links.length > 0) ||
                  (currentEvent.imageGallery && currentEvent.imageGallery.length > 0)
                )
              ) {
                setSelectedEvent(currentEvent);
              }
            }}
          >
            <div className="min-h-[24px] sm:min-h-[28px] flex items-center justify-center">
              {currentEvent && (
                <div
                  className="text-white text-base sm:text-[20px] font-medium leading-[1.2] tracking-[-0.02em] text-center event-title"
                  key={currentMonth}
                >
                  {currentEvent.title}
                </div>
              )}
            </div>

            {/* Project/Role Image - always reserve space to prevent layout shift */}
            <div
              className="w-full aspect-[2/1] bg-white/[0.13] overflow-hidden event-image"
              style={{ opacity: currentEvent ? 1 : 0 }}
            >
              {currentEvent?.image && (
                currentEvent.imageUrl?.includes('.svg') ? (
                  <img
                    src={currentEvent.imageUrl}
                    alt={currentEvent.title}
                    className="w-full h-full object-cover"
                    key={currentMonth}
                  />
                ) : (
                  <img
                    src={urlFor(currentEvent.image).width(400).auto('format').url()}
                    srcSet={`${urlFor(currentEvent.image).width(400).auto('format').url()} 1x, ${urlFor(currentEvent.image).width(800).auto('format').url()} 2x, ${urlFor(currentEvent.image).width(1200).auto('format').url()} 3x`}
                    alt={currentEvent.title}
                    className="w-full h-full object-cover"
                    key={currentMonth}
                  />
                )
              )}
            </div>
          </div>
        </div>

        {/* Dial Viewport - Large interactive zone for intuitive scrolling */}
        <div
          className="w-full max-w-full px-4 sm:px-8 md:max-w-4xl md:px-0 mt-12 pt-12 pb-12 sm:pb-6"
        >
          <div
            ref={containerRef}
            className={`relative h-20 sm:h-24 md:h-32 overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ touchAction: 'pan-x' }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {/* Arrow pointer at top - always centered */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 pointer-events-none z-10">
              <div
                className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white opacity-80"
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}
              />
            </div>

            {/* Needle line extending from arrow to bar baseline */}
            <div
              className="absolute left-1/2 -translate-x-1/2 bg-white pointer-events-none z-10"
              style={{
                width: '1px',
                top: '8px',
                bottom: '0',
                opacity: 0.3
              }}
            />

            {/* Scrolling timeline */}
            <div
              className="absolute bottom-0 flex items-end h-full"
              style={{
                left: '50%',
                transform: `translateX(${-currentMonth * barSpacing - barSpacing / 2 + (isMomentumScrolling ? momentumOffset : 0)}px)`,
                transition: (isAnimating || isDragging || isMomentumScrolling) ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              {Array.from({ length: totalMonths }).map((_, i) => {
                const isYear = i % 12 === 0;
                const hasEvent = timelineEvents[i];

                // Calculate which bar is current - only use currentMonth, not drag offset
                const isCurrent = i === currentMonth;

                let height = 'h-3 sm:h-4';
                let backgroundColor = 'oklch(0.65 0 0)'; // Gray for regular months

                if (isYear) {
                  height = 'h-8 sm:h-10 md:h-12';
                  backgroundColor = 'oklch(1 0 0)'; // Pure white for year bars
                } else if (hasEvent) {
                  backgroundColor = hasEvent.color;
                }

                if (isCurrent) {
                  if (isYear) {
                    backgroundColor = 'oklch(1 0 0)'; // Keep pure white for current year
                  }
                }

                return (
                  <div
                    key={i}
                    data-month={i}
                    onClick={() => animateToMonth(i)}
                    className="relative flex-shrink-0 cursor-pointer group"
                    style={{ width: `${barSpacing}px` }} // Match barSpacing for proper alignment
                  >
                    {/* Invisible larger touch target - expanded horizontally for better mobile UX */}
                    <div className="absolute inset-0 -left-2 -right-2 -top-6 -bottom-6 sm:-left-2 sm:-right-2 sm:-top-4 sm:-bottom-4" />

                    {/* Actual visible bar - centered in the touch area */}
                    <div
                      className={`${height} w-0.5 sm:w-0.5 mx-auto timeline-bar ${isCurrent ? 'scale-125 sm:scale-150' : 'scale-100'}`}
                      style={{
                        backgroundColor,
                        opacity: isCurrent ? 1 : (hasEvent ? 0.8 : 0.6),
                        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease, opacity 0.2s ease'
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Gradient fade edges - responsive width */}
            <div
              className="absolute left-0 top-0 bottom-0 w-9 sm:w-24 md:w-48 bg-gradient-to-r from-black via-black/80 to-transparent pointer-events-none z-10"
              style={{ backdropFilter: 'blur(2px)' }}
            />
            <div
              className="absolute right-0 top-0 bottom-0 w-9 sm:w-24 md:w-48 bg-gradient-to-l from-black via-black/80 to-transparent pointer-events-none z-10"
              style={{ backdropFilter: 'blur(2px)' }}
            />
          </div>

          {/* Progress indicator */}
          <div className="mt-4 sm:mt-6 text-center text-[#525252] text-xs sm:text-sm font-medium">
            Month {currentMonth + 1} of {totalMonths}
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-0 sm:mt-4 flex items-center justify-center gap-3">
          {settings?.email && (
            <>
              <a href={`mailto:${settings.email}`} className="text-[#959595] text-base font-medium leading-[1.2] tracking-[-0.02em] hover:text-white transition-colors">
                Email
              </a>
              <span className="text-white text-base font-medium leading-[1.2] tracking-[-0.02em]">/</span>
            </>
          )}
          {settings?.linkedinUrl && (
            <>
              <a href={settings.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#959595] text-base font-medium leading-[1.2] tracking-[-0.02em] hover:text-white transition-colors">
                Linkedin
              </a>
              <span className="text-white text-base font-medium leading-[1.2] tracking-[-0.02em]">/</span>
            </>
          )}
          {settings?.showAndTellUrl && (
            <>
              <a href={settings.showAndTellUrl} className="text-[#959595] text-base font-medium leading-[1.2] tracking-[-0.02em] hover:text-white transition-colors">
                Show & tell
              </a>
              <span className="text-white text-base font-medium leading-[1.2] tracking-[-0.02em]">/</span>
            </>
          )}
          <a
            href="/portfolio"
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/portfolio');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="text-[#959595] text-base font-medium leading-[1.2] tracking-[-0.02em] hover:text-white transition-colors"
          >
            Portfolio
          </a>
          <span className="text-white text-base font-medium leading-[1.2] tracking-[-0.02em]">/</span>
          <a
            href="/classic"
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/classic');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="text-[#959595] text-base font-medium leading-[1.2] tracking-[-0.02em] hover:text-white transition-colors"
          >
            Classic view
          </a>
        </div>
      </section>

      {/* Pagination - Fixed at top */}
      <div className="fixed top-8 sm:top-12 left-1/2 -translate-x-1/2 flex gap-[5px] z-50">
        <button
          onClick={() => scrollToSection(0)}
          className={`flex items-center justify-center px-1 py-1.5 backdrop-blur-sm ${currentSection === 0 ? 'bg-[#F5B50A]' : 'bg-[rgba(255,255,255,0.12)]'}`}
        >
          <span className={`text-xs sm:text-sm font-medium leading-none tracking-[-0.02em] h-[10px] flex items-center ${currentSection === 0 ? 'text-black' : 'text-white'}`}>Intro</span>
        </button>
        <button
          onClick={() => scrollToSection(1)}
          className={`flex items-center justify-center px-1 py-1.5 backdrop-blur-sm ${currentSection === 1 ? 'bg-[#F5B50A]' : 'bg-[rgba(255,255,255,0.12)]'}`}
        >
          <span className={`text-xs sm:text-sm font-medium leading-none tracking-[-0.02em] h-[10px] flex items-center ${currentSection === 1 ? 'text-black' : 'text-white'}`}>Context</span>
        </button>
        <button
          onClick={() => scrollToSection(2)}
          className={`flex items-center justify-center px-1 py-1.5 backdrop-blur-sm ${currentSection === 2 ? 'bg-[#F5B50A]' : 'bg-[rgba(255,255,255,0.12)]'}`}
        >
          <span className={`text-xs sm:text-sm font-medium leading-none tracking-[-0.02em] h-[10px] flex items-center ${currentSection === 2 ? 'text-black' : 'text-white'}`}>Timeline</span>
        </button>
      </div>

      <style>{`
        body, html {
          overscroll-behavior: none;
          background-color: #000000;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (min-width: 640px) {
          .year-display { 
            font-size: 96px !important; 
          }
        }
        /* Hide scrollbar for Chrome, Safari and Opera */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        /* Hide scrollbar for IE, Edge and Firefox */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        /* Smooth scroll for section navigation only */
        @media (prefers-reduced-motion: no-preference) {
          .bg-black {
            scroll-behavior: smooth;
          }
        }
      `}</style>

      <EventModal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        event={selectedEvent}
      />

      {/* Unified Control Dock - Only show if any of the navigation features are enabled */}
      {(flags.minimap || flags.zoomControls || flags.quickJump || flags.autoPlay) && (
        <ControlDock
          totalMonths={totalMonths}
          currentMonth={currentMonth}
          events={timelineEvents}
          onMonthSelect={animateToMonth}
          currentZoom={barSpacing}
          onZoomChange={setBarSpacing}
          isPlaying={isPlaying}
          onTogglePlay={toggleAutoPlay}
          startYear={startYear}
          endYear={endYear}
          currentYear={year}
          onYearSelect={jumpToYear}
          flags={flags}
        />
      )}

      <SettingsPanel
        flags={flags}
        toggleFlag={toggleFlag}
        resetToDefault={resetToDefault}
      />
    </div>
  );
};

export default CareerDial;